// Reading a programme someone already has into import rows (programme
// import). The model only transcribes: utils/programmeImport.js turns the
// rows into sessions, so timings and structure come from the same code as a
// spreadsheet in Khronos's own template. Pure, so Jest can import it.

const FIELDS = ["week", "day", "session", "part", "exercise", "sets", "amount", "load", "rpe", "rest", "notes", "role"];

export const PROGRAMME_ROWS_SCHEMA = {
  type: "object",
  properties: {
    title: { type: "string" },
    rows: {
      type: "array",
      items: {
        type: "object",
        properties: Object.fromEntries(FIELDS.map((f) => [f, { type: "string" }])),
        required: ["week", "day", "session", "exercise", "sets", "amount"],
      },
    },
  },
  required: ["rows"],
};

export const PROGRAMME_ROWS_PROMPT = `You are transcribing a training programme a coach wrote into rows, one row per exercise, exactly as written. Copy; never invent, improve, add or drop anything. If something is unclear, copy the words into "notes".

Return JSON: {"title": "<programme name if stated>", "rows": [ ... ]}. Every row has these string fields:
- "week": the week number, or a range when the same row repeats unchanged ("1-4").
- "day": the weekday ("Mon" ... "Sun"), or "1" to "7" for "Day 1" style programmes.
- "session": the session's name as written ("Gym A", "Erg intervals"). Rows of one session share it exactly.
- "part": the block it sits in ("Warm-up", "Main", "Cool-down", "A1", "P1 · Hinge"), or "".
- "exercise": the exercise name only.
- "sets": the number of sets or rounds ("3"); "1" when not stated.
- "amount": one set's work, in one of these forms: "10" (reps), "10 each side", "45 s", "6 min", "30 m" (metres), "2 km", "15 cal", "5 / 3 / 2" (reps per set), "40 s on / 90 s easy". Use "min" for minutes and "m" only for metres.
- "load": the load or intensity as written ("60 kg", "75% 1RM", "Bodyweight"), or "".
- "rpe": the RPE or effort as written, or "".
- "rest": the rest between sets ("90 s", "2 min"), or "".
- "notes": tempo, coaching cues and anything else for that exercise, or "".
- "role": only for rows meant for one role in a team (for example "sweep" or "drummer"); otherwise "".

Rules:
- Keep the order exercises are done in. When sides take turns ("left, then right, repeat"), write a LEFT row then a RIGHT row with the same sets and amount.
- Sessions that are only a reminder (club training, a rest day) are still one row each, with the words in "exercise".
- Leave out anything the athlete fills in (what they lifted, done ticks, their own notes) and anything that isn't the programme (instructions, summaries, tables of totals).

The programme:
`;

/** The model's rows, kept only where they name an exercise, as import rows. */
export const parseProgrammeRows = (result) => {
  const rows = Array.isArray(result?.rows) ? result.rows : [];
  return {
    title: typeof result?.title === "string" ? result.title.trim().slice(0, 120) : "",
    rows: rows
      .filter((r) => r && typeof r === "object" && String(r.exercise || "").trim())
      .map((r) => {
        const row = Object.fromEntries(FIELDS.map((f) => [f, String(r[f] ?? "").trim()]));
        return { ...row, role: row.role ? row.role.toLowerCase() : null };
      }),
  };
};

// Columns the athlete fills in; their contents never go to the model.
const ATHLETE_COLUMN_RE = /^(my|done|kg used|completed|actual|logged|video)\b/i;

/**
 * A sheet as compact text for the model: blank rows and columns gone, and
 * the athlete's own columns left out when a header names them. Cut at
 * `max` characters; `cut` says whether it was.
 */
export const sheetText = (sheet, max = 40000) => {
  const rows = (sheet?.rows || []).filter((r) => r.some(Boolean));
  const header = rows.slice(0, 40).find((r) => r.some((c) => /^exercise/i.test(c)));
  const drop = new Set(
    header ? header.flatMap((c, i) => (ATHLETE_COLUMN_RE.test(String(c).trim()) ? [i] : [])) : [],
  );
  let text = `Sheet: ${sheet?.name || ""}\n`;
  for (const row of rows) {
    const line = row
      .map((c, i) => (drop.has(i) ? "" : String(c).replace(/\s+/g, " ")))
      .join("\t")
      .replace(/\t+$/, "");
    if (text.length + line.length > max) return { text, cut: true };
    text += `${line}\n`;
  }
  return { text, cut: false };
};
